import type { ShipType } from '../constants';
import type { HexCoord, HexVec } from '../hex';
import type { GameId, OrdnanceId, ShipId } from '../ids';
import type { Phase } from '../types';
import type {
  AstrogationOrder,
  AttackType,
  DamageType,
  FleetPurchase,
  GravityEffect,
  OrdnanceLaunch,
  OrdnanceType,
  PlayerId,
  ShipLifecycle,
  TransferOrder,
} from '../types/domain';

// Granular domain events emitted by engine functions.
export type EngineEvent =
  // Game lifecycle
  | {
      type: 'gameCreated';
      scenario: string;
      turn: number;
      phase: Phase;
      matchSeed: number;
    }
  | {
      type: 'phaseChanged';
      phase: Phase;
      turn: number;
      activePlayer: PlayerId;
    }
  | {
      type: 'turnAdvanced';
      turn: number;
      activePlayer: PlayerId;
    }
  | {
      type: 'gameOver';
      winner: PlayerId | null;
      reason: string;
    }

  // Fleet building
  | {
      type: 'fleetPurchased';
      playerId: PlayerId;
      purchases: FleetPurchase[];
      shipTypes: ShipType[];
    }
  | {
      type: 'astrogationOrdersCommitted';
      playerId: PlayerId;
      orders: AstrogationOrder[];
    }
  | {
      type: 'ordnanceLaunchesCommitted';
      playerId: PlayerId;
      launches: OrdnanceLaunch[];
    }

  // Ship movement
  | {
      type: 'shipMoved';
      shipId: ShipId;
      from: HexCoord;
      to: HexCoord;
      path: HexCoord[];
      fuelSpent: number;
      fuelRemaining: number;
      newVelocity: HexVec;
      lifecycle: ShipLifecycle;
      overloadUsed: boolean;
      lastBurnDirection?: number;
      pendingGravityEffects: GravityEffect[];
    }
  | {
      type: 'shipLanded';
      shipId: ShipId;
      landedAt: string;
    }
  | {
      type: 'shipCrashed';
      shipId: ShipId;
      hex: HexCoord;
    }
  | {
      type: 'shipResupplied';
      shipId: ShipId;
      source: 'base' | 'orbitalBase';
      sourceId?: ShipId;
    }
  | {
      type: 'shipCaptured';
      shipId: ShipId;
      capturedBy: PlayerId;
      capturedByShipId: ShipId;
    }
  | {
      type: 'shipDestroyed';
      shipId: ShipId;
      cause: string;
      // Exact live attribution, carried verbatim so projection can
      // reconstruct kill credit: `cause` is the attack label (e.g.
      // 'asteroidHazard') while `deathCause` is the engine's exact
      // value (e.g. 'asteroid'), and `killedBy` is the attacker id
      // (null for environmental deaths). Optional: older archived
      // streams omit them and the projector keeps legacy behavior.
      deathCause?: string;
      killedBy?: ShipId | null;
    }
  | {
      type: 'asteroidDestroyed';
      hex: HexCoord;
    }
  | {
      type: 'baseDestroyed';
      hex: HexCoord;
    }

  // Ramming
  | {
      type: 'ramming';
      shipId: ShipId;
      otherShipId: ShipId;
      hex: HexCoord;
      roll: number;
      damageType: DamageType;
      disabledTurns: number;
    }

  // Ordnance
  | {
      type: 'ordnanceLaunched';
      ordnanceId: OrdnanceId;
      ordnanceType: OrdnanceType;
      owner: PlayerId;
      sourceShipId: ShipId;
      position: HexCoord;
      velocity: HexVec;
      turnsRemaining: number;
      pendingGravityEffects: GravityEffect[];
    }
  | {
      type: 'ordnanceMoved';
      ordnanceId: OrdnanceId;
      position: HexCoord;
      velocity: HexVec;
      turnsRemaining: number;
      pendingGravityEffects: GravityEffect[];
    }
  | {
      type: 'ordnanceDetonated';
      ordnanceId: OrdnanceId;
      ordnanceType: OrdnanceType;
      hex: HexCoord;
      targetShipId?: ShipId;
      roll: number;
      damageType: DamageType;
      disabledTurns: number;
    }
  | {
      type: 'ordnanceDestroyed';
      ordnanceId: OrdnanceId;
      cause: string;
    }
  | {
      type: 'ordnanceExpired';
      ordnanceId: OrdnanceId;
    }

  // Combat
  | {
      type: 'combatAttack';
      attackerIds: ShipId[];
      targetId: ShipId | OrdnanceId;
      targetType: 'ship' | 'ordnance';
      attackType: AttackType;
      odds?: string;
      attackStrength?: number;
      defendStrength?: number;
      rangeMod?: number;
      velocityMod?: number;
      roll: number;
      modifiedRoll: number;
      damageType: DamageType;
      disabledTurns: number;
    }

  // Logistics
  | {
      type: 'fuelTransferred';
      fromShipId: ShipId;
      toShipId: ShipId;
      amount: number;
    }
  | {
      type: 'cargoTransferred';
      fromShipId: ShipId;
      toShipId: ShipId;
      amount: number;
    }
  | {
      type: 'passengersTransferred';
      fromShipId: ShipId;
      toShipId: ShipId;
      amount: number;
    }
  | {
      type: 'shipSurrendered';
      shipId: ShipId;
    }
  | {
      type: 'logisticsTransfersCommitted';
      playerId: PlayerId;
      transfers: TransferOrder[];
    }
  | {
      type: 'surrenderDeclared';
      playerId: PlayerId;
      shipIds: ShipId[];
    }
  | {
      type: 'baseEmplaced';
      shipId: ShipId;
      sourceShipId: ShipId;
      owner: PlayerId;
      position: HexCoord;
      velocity: HexVec;
    }

  // Hidden identity / race
  | {
      type: 'fugitiveDesignated';
      shipId: ShipId;
      playerId: PlayerId;
    }
  | {
      type: 'identityRevealed';
      shipId: ShipId;
    }
  | {
      type: 'checkpointVisited';
      playerId: PlayerId;
      body: string;
    };

// Versioned event envelope for match-scoped persistence.
export interface EventEnvelope {
  gameId: GameId;
  seq: number;
  ts: number;
  actor: PlayerId | null;
  event: EngineEvent;
}

export type CombatAttackEvent = Extract<EngineEvent, { type: 'combatAttack' }>;
export type ShipTargetCombatAttackEvent = CombatAttackEvent & {
  targetType: 'ship';
  targetId: ShipId;
};
export type OrdnanceTargetCombatAttackEvent = CombatAttackEvent & {
  targetType: 'ordnance';
  targetId: OrdnanceId;
};

export const isShipTargetCombatAttackEvent = (
  event: CombatAttackEvent,
): event is ShipTargetCombatAttackEvent => event.targetType === 'ship';

// Build a shipDestroyed event from a just-destroyed ship, carrying its
// exact deathCause/killedBy so projection reconstructs kill attribution
// verbatim. Fields the live ship never set stay absent from the event.
export const shipDestroyedEvent = (
  ship: { id: ShipId; deathCause?: string; killedBy?: ShipId | null },
  cause: string,
): Extract<EngineEvent, { type: 'shipDestroyed' }> => ({
  type: 'shipDestroyed',
  shipId: ship.id,
  cause,
  ...(ship.deathCause !== undefined ? { deathCause: ship.deathCause } : {}),
  ...(ship.killedBy !== undefined ? { killedBy: ship.killedBy } : {}),
});
